import { KeyValue } from "@/main/models/common";
import { AssociatedSupplierResponse } from "@/main/models/response/bodyshopResponse";

export interface ISelfSuppliedFormValues {
	mainSupplier: KeyValue | null;
	selfSupplied: boolean;
}

export interface ISelfSuppliedGrants {
	canSetMainSupplier: boolean;
	canSetSelfSupplied: boolean;
}

export function formatCompanyAndTradeName(companyName: string, tradeName?: string | null): string {
	return tradeName ? `${companyName} / ${tradeName}` : companyName;
}

export function buildAssociatedSupplierOptions(suppliers: AssociatedSupplierResponse[]): KeyValue[] {
	return suppliers.map((supplier) => ({
		key: supplier.guid,
		value: formatCompanyAndTradeName(supplier.companyName, supplier.tradeName),
	}));
}

export function buildSelfSuppliedRequestFields(values: ISelfSuppliedFormValues, grants: ISelfSuppliedGrants) {
	return {
		...(grants.canSetMainSupplier && values.mainSupplier ? { guidMainSupplier: String(values.mainSupplier.key) } : {}),
		...(grants.canSetSelfSupplied ? { selfSupplied: values.selfSupplied } : {}),
	};
}

export function resolveSelfSuppliedOnRegistrationNumberChange(
	isRegistrationNumberChanged: boolean,
	loadedSelfSupplied: boolean,
): boolean {
	return isRegistrationNumberChanged ? false : loadedSelfSupplied;
}

export function shouldConfirmRegistrationNumberChange(
	isEdit: boolean,
	loadedRegistrationNumber: string,
	currentRegistrationNumber: string,
	hasAssociatedSuppliers: boolean,
): boolean {
	return isEdit && hasAssociatedSuppliers && loadedRegistrationNumber !== currentRegistrationNumber;
}

export function isMainSupplierStillAssociated(mainSupplier: KeyValue | null, associatedOptions: KeyValue[]): boolean {
	return mainSupplier === null || associatedOptions.some((option) => option.key === mainSupplier.key);
}

export type PreSaveOutcome = "proceed" | "mainSupplierNotAssociated" | "registrationNumberChangeCancelled";

export interface IPreSaveInput {
	isEdit: boolean;
	canSetMainSupplier: boolean;
	mainSupplier: KeyValue | null;
	loadedRegistrationNumber: string;
	currentRegistrationNumber: string;
	hasAssociatedSuppliers: boolean;
}

export interface IPreSaveDependencies {
	refreshAssociatedOptions: () => Promise<KeyValue[]>;
	confirmRegistrationNumberChange: () => Promise<boolean>;
}

export async function evaluatePreSaveChecks(
	input: IPreSaveInput,
	dependencies: IPreSaveDependencies,
): Promise<PreSaveOutcome> {
	const isRegistrationNumberChanged = input.loadedRegistrationNumber !== input.currentRegistrationNumber;

	if (input.canSetMainSupplier && input.mainSupplier && !isRegistrationNumberChanged) {
		const freshOptions = await dependencies.refreshAssociatedOptions();
		if (!isMainSupplierStillAssociated(input.mainSupplier, freshOptions)) {
			return "mainSupplierNotAssociated";
		}
	}

	if (
		shouldConfirmRegistrationNumberChange(
			input.isEdit,
			input.loadedRegistrationNumber,
			input.currentRegistrationNumber,
			input.hasAssociatedSuppliers,
		)
	) {
		const isConfirmed = await dependencies.confirmRegistrationNumberChange();
		if (!isConfirmed) return "registrationNumberChangeCancelled";
	}

	return "proceed";
}

export function hasMainSupplierRefusal(
	errorMessages: { key: string; value: string }[] | undefined,
	refusalText: string,
) {
	return (errorMessages ?? []).some((message) => message.value === refusalText);
}
